

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTravelContactModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact_id: '',
        account_id: '',
        phone: '',
        email: '',
        primary_contact: false,
        allergy_condition: '',
        dietary_preference: '',
        address: '',
        passport_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        passport_issuing_country: '',
        record_type: 'Passenger'
    });

    const [formErrors, setFormErrors] = useState({});
    const [contacts, setContacts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchContacts();
            fetchAccounts();
        }
    }, [isOpen]);

    useEffect(() => {
        if (formData.contact_id && contacts.length > 0) {
            const contact = contacts.find(c => c.id === parseInt(formData.contact_id));
            if (contact) {
                setSelectedContact(contact);
                // Auto-fill fields from selected contact
                setFormData(prev => ({
                    ...prev,
                    email: prev.email || contact.email || '',
                    phone: prev.phone || contact.phone || contact.mobile_number || '',
                    account_id: prev.account_id || contact.account_id || ''
                }));
            }
        }
    }, [formData.contact_id, contacts]);

    const fetchContacts = async () => {
        setLoadingContacts(true);
        try {
            const response = await axiosInstance.get('/contacts/');
            setContacts(response.data || []);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoadingContacts(false);
        }
    };

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setAccounts(response.data || []);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Contact Name is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                contact_id: formData.contact_id ? parseInt(formData.contact_id) : null,
                account_id: formData.account_id ? parseInt(formData.account_id) : null,
                passport_issue_date: formData.passport_issue_date || null,
                passport_expiry_date: formData.passport_expiry_date || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                contact_id: '',
                account_id: '',
                phone: '',
                email: '',
                primary_contact: false,
                allergy_condition: '',
                dietary_preference: '',
                address: '',
                passport_number: '',
                passport_issue_date: '',
                passport_expiry_date: '',
                passport_issuing_country: '',
                record_type: 'Passenger'
            });
            setSelectedContact(null);
        } catch (error) {
            console.error('Error saving Travel Contact:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Travel Contact',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const isPassengerOrGuide = formData.record_type === 'Passenger' || formData.record_type === 'Guide';
    const isPassenger = formData.record_type === 'Passenger';

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Travel Contact</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Contact Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Record Type</InputLabel>
                                        <Select
                                            name="record_type"
                                            value={formData.record_type}
                                            onChange={handleInputChange}
                                            label="Record Type"

                                        >
                                            <MenuItem value="Passenger">Passenger</MenuItem>
                                            <MenuItem value="Guide">Guide</MenuItem>
                                            <MenuItem value="Merchant">Merchant</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Select Contact</InputLabel>
                                        <Select
                                            name="contact_id"
                                            value={formData.contact_id}
                                            onChange={handleInputChange}
                                            label="Select Contact"

                                            disabled={loadingContacts}
                                        >
                                            <MenuItem value=""><em>Select a Contact (Optional)</em></MenuItem>
                                            {contacts.map(contact => (
                                                <MenuItem key={contact.id} value={contact.id}>
                                                    {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || `Contact #${contact.id}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Account</InputLabel>
                                        <Select
                                            name="account_id"
                                            value={formData.account_id}
                                            onChange={handleInputChange}
                                            label="Account"

                                            disabled={loadingAccounts}
                                        >
                                            <MenuItem value=""><em>Select an Account</em></MenuItem>
                                            {accounts.map(acc => (
                                                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.primary_contact} name="primary_contact" onChange={handleInputChange} />}
                                            label="Primary Contact"
                                        />
                                    </MDBox>
                                </Grid>

                                {isPassengerOrGuide && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Passport Number"
                                                name="passport_number"
                                                value={formData.passport_number}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Passport Issue Date"
                                                name="passport_issue_date"
                                                type="date"
                                                value={formData.passport_issue_date}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Passport Expiry Date"
                                                name="passport_expiry_date"
                                                type="date"
                                                value={formData.passport_expiry_date}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Passport Issuing Country"
                                                name="passport_issuing_country"
                                                value={formData.passport_issuing_country}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>
                                    </>
                                )}

                                {isPassenger && (
                                    <>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Allergies"
                                                name="allergy_condition"
                                                multiline
                                                rows={3}
                                                value={formData.allergy_condition}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Dietary Preferences"
                                                name="dietary_preference"
                                                multiline
                                                rows={3}
                                                value={formData.dietary_preference}
                                                onChange={handleInputChange}
                                                variant="outlined"
                                        margin="normal"
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        name="address"
                                        multiline
                                        rows={3}
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Travel Contact'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTravelContactModal;

